// utils/experienceTotal.js
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

// Accepts "MMM YYYY" or "MMMM YYYY" (e.g., "Jan 2022" or "January 2022")
// Returns a month index you can compare/sort with (e.g., 2022-01 -> 2022*12+1 → 24265)
const MONTH_FORMATS = ["MMM YYYY", "MMMM YYYY"];
const monthIndex = (y, m /* 1-12 */) => y * 12 + (m - 1);

const parseMonthIndex = (dateString) => {
  if (!dateString) return null;

  // Handle "Present"
  if (
    typeof dateString === "string" &&
    dateString.trim().toLowerCase() === "present"
  ) {
    const now = dayjs();
    return monthIndex(now.year(), now.month() + 1);
  }

  // Normalize capitalization like your parseFormattedDate does
  const parts = dateString.trim().split(/\s+/);
  if (parts.length !== 2) return null;
  const month =
    parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
  const year = parts[1];
  const normalized = `${month} ${year}`;

  for (const fmt of MONTH_FORMATS) {
    const d = dayjs(normalized, fmt, true);
    if (d.isValid()) return monthIndex(d.year(), d.month() + 1);
  }
  return null;
};

/**
 * Calculate total experience across experiences by merging overlapping month ranges.
 * @param {Array<{
 *   Work_start_date: string | null,
 *   Work_end_date: string | "Present" | null
 * }>} experiences
 * @param {Object} opts
 * @param {boolean} opts.inclusive  - count both start and end months (default: true)
 * @returns {{ totalMonths: number, totalYears: number, remainderMonths: number, formatted: string }}
 */
export const computeTotalExperience = (
  experiences = [],
  { inclusive = true } = {}
) => {
  // 1) Collect valid [startIdx, endIdx] intervals
  const rawIntervals = [];
  for (const ex of experiences || []) {
    let s = parseMonthIndex(ex?.Work_start_date);
    let e = parseMonthIndex(ex?.Work_end_date);
    if (s == null) continue;
    if (e == null) {
      // If no end provided and not "Present", treat as single-month start; or skip. Here we treat as ongoing "Present".
      e = parseMonthIndex("Present");
    }
    if (e < s) {
      // swap if user accidentally reversed them
      const tmp = e;
      e = s;
      s = tmp;
    }
    rawIntervals.push([s, e]);
  }

  if (rawIntervals.length === 0) {
    return {
      totalMonths: 0,
      totalYears: 0,
      remainderMonths: 0,
      formatted: "0 yrs",
    };
  }

  // 2) Sort by start then merge overlaps
  rawIntervals.sort((a, b) => a[0] - b[0]);
  const merged = [];
  let [curS, curE] = rawIntervals[0];

  for (let i = 1; i < rawIntervals.length; i++) {
    const [s, e] = rawIntervals[i];
    if (s <= curE + 1) {
      // overlap or touching (e.g., back-to-back months) → merge
      curE = Math.max(curE, e);
    } else {
      merged.push([curS, curE]);
      [curS, curE] = [s, e];
    }
  }
  merged.push([curS, curE]);

  // 3) Sum months (inclusive means +1 per range)
  const totalMonths = merged.reduce((acc, [s, e]) => {
    const span = e - s + (inclusive ? 1 : 0);
    return acc + Math.max(span, 0);
  }, 0);

  const totalYears = Math.floor(totalMonths / 12);
  const remainderMonths = totalMonths % 12;
  const formatted =
    totalYears > 0
      ? `${totalYears} yr${totalYears > 1 ? "s" : ""}${
          remainderMonths
            ? ` ${remainderMonths} month${remainderMonths > 1 ? "s" : ""}`
            : ""
        }`
      : `${remainderMonths} mo${remainderMonths !== 1 ? "s" : ""}`;

  return { totalMonths, totalYears, remainderMonths, formatted };
}
